import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  FormControlLabel,
  Grid,
  Paper,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../services/api";

const AdicionarTipoLicenca = () => {
  useTheme();
  const navigate = useNavigate();

  const [codigo, setCodigo] = useState("");
  const [nome, setNome] = useState("");
  const [ativo, setAtivo] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [tiposLicenca, setTiposLicenca] = useState([]);
  const [filtroNome, setFiltroNome] = useState("");
  const [loadingLista, setLoadingLista] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [editingTarget, setEditingTarget] = useState(null);

  useEffect(() => {
    carregarTiposLicenca();
  }, []);

  const carregarTiposLicenca = async (nome) => {
    try {
      setLoadingLista(true);
      const response = await api.get("/TipoLicenca/GetByNomeAsync", {
        params: { nome },
      });
      setTiposLicenca(
        Array.isArray(response.data)
          ? response.data
          : response.data?.data || [],
      );
    } catch (err) {
      console.error("Erro ao carregar tipos de licença:", err);
    } finally {
      setLoadingLista(false);
    }
  };

  const tiposFiltrados = tiposLicenca.filter((tipo) =>
    tipo.nome?.toLowerCase().includes(filtroNome.toLowerCase()),
  );

  const abrirDialogDelete = (tipo) => {
    setDeleteTarget(tipo);
    setDeleteDialogOpen(true);
  };

  const fecharDialogDelete = () => {
    setDeleteTarget(null);
    setDeleteDialogOpen(false);
  };

  const confirmarDelete = async () => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);

      await api.delete(`/TipoLicenca/${deleteTarget.id}`);

      setTiposLicenca((prev) => prev.filter((t) => t.id !== deleteTarget.id));
      fecharDialogDelete();
    } catch (err) {
      console.error("Erro ao deletar tipo de licença:", err);
      setError(
        err?.response?.data?.detail ||
          err?.response?.data ||
          "Erro ao deletar.",
      );
    } finally {
      setDeleting(false);
    }
  };

  const iniciarEdicao = (tipo) => {
    setEditingTarget(tipo);
    setCodigo(tipo.codigo ?? tipo.Codigo ?? "");
    setNome(tipo.nome ?? tipo.Nome ?? "");
    setAtivo(tipo.isActive ?? tipo.IsActive ?? tipo.ativo ?? true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancelarEdicao = () => {
    setEditingTarget(null);
    setCodigo("");
    setNome("");
    setAtivo(true);
    setError(null);
    setSuccess(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!codigo?.trim() || !nome?.trim()) {
      setError("Preencha os campos Código e Nome.");
      return;
    }

    try {
      setLoading(true);
      const payload = {
        Id: editingTarget?.id,
        Codigo: codigo.trim(),
        Nome: nome.trim(),
        IsActive: !!ativo,
      };

      if (editingTarget) {
        await api.put(`/TipoLicenca/`, payload);
        setTiposLicenca((prev) =>
          prev.map((t) =>
            t.id === editingTarget.id
              ? {
                  ...t,
                  codigo: payload.Codigo,
                  nome: payload.Nome,
                  isActive: payload.IsActive,
                }
              : t,
          ),
        );
        setSuccess("Tipo de licença atualizado com sucesso.");
        setEditingTarget(null);
      } else {
        await api.post("/TipoLicenca/", payload);
        setSuccess("Tipo de licença adicionado com sucesso.");
        carregarTiposLicenca();
      }

      setCodigo("");
      setNome("");
      setAtivo(true);
    } catch (err) {
      console.error("Erro ao salvar tipo de licença:", err);
      setError(
        err?.response?.data?.detail || err?.response?.data || "Erro ao salvar.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container
      maxWidth="md"
      sx={{
        mt: { xs: 2, md: 4 },
        mb: { xs: 2, md: 4 },
        px: { xs: 2, sm: 3, md: 4 },
      }}
    >
      <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 2 }} elevation={3}>
        <Box sx={{ mb: 2, display: "flex", alignItems: "center" }}>
          <Typography
            variant="h5"
            component="h1"
            fontWeight="bold"
            color="primary.main"
            sx={{ fontSize: { xs: "1.2rem", sm: "1.5rem" } }}
          >
            Adicionar Tipo de Licença
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} noValidate>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Código"
                placeholder="Código interno do tipo de licença (ex: PREVIA, INSTALACAO)"
                value={codigo}
                onChange={(e) => setCodigo(e.target.value)}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Nome"
                placeholder="Nome de exibição do tipo de licença (ex: Licença Prévia)"
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                fullWidth
                required
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={ativo}
                    onChange={(e) => setAtivo(e.target.checked)}
                    color="primary"
                  />
                }
                label={`Ativo: ${ativo ? "Sim" : "Não"}`}
              />
            </Grid>

            <Grid item xs={12}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                sx={{ width: { xs: "100%", sm: "auto" } }}
              >
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading}
                  sx={{ textTransform: "none", width: { xs: "100%", sm: "auto" } }}
                >
                  {loading ? <CircularProgress size={20} /> : "Salvar"}
                </Button>
                <Button
                  variant="outlined"
                  sx={{ textTransform: "none", width: { xs: "100%", sm: "auto" } }}
                  onClick={editingTarget ? cancelarEdicao : () => navigate(-1)}
                >
                  {editingTarget ? "Cancelar Edição" : "Cancelar"}
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Box>
      </Paper>

      {/* Seção de Listagem */}
      <Paper
        sx={{ p: { xs: 2, md: 3 }, borderRadius: 2, mt: { xs: 3, md: 4 } }}
        elevation={3}
      >
        <Box
          sx={{
            mb: 3,
            display: "flex",
            alignItems: { xs: "flex-start", sm: "center" },
            justifyContent: "space-between",
            flexDirection: { xs: "column", sm: "row" },
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight="bold">
              Tipos de Licença Existentes
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Ações
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Total: {tiposFiltrados.length}
          </Typography>
        </Box>

        <Box sx={{ mb: 3 }}>
          <TextField
            label="Filtrar por Nome"
            placeholder="Digite o nome para filtrar..."
            value={filtroNome}
            onChange={(e) => setFiltroNome(e.target.value)}
            fullWidth
            size="small"
          />
        </Box>

        {loadingLista ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress />
          </Box>
        ) : tiposFiltrados.length === 0 ? (
          <Typography color="text.secondary" align="center" sx={{ py: 2 }}>
            Nenhum tipo de licença encontrado.
          </Typography>
        ) : (
          <TableContainer
            component={Paper}
            variant="outlined"
            sx={{ overflowX: "auto" }}
          >
            <Table size="small">
              <TableHead sx={{ bgcolor: "action.hover" }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold" }}>Código</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Nome</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }} align="center">
                    Ativo
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold" }} align="center">
                    Ações
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tiposFiltrados.map((tipo) => (
                  <TableRow key={tipo.id} hover>
                    <TableCell>{tipo.codigo}</TableCell>
                    <TableCell>{tipo.nome}</TableCell>
                    <TableCell align="center">
                      {(tipo.isActive ?? tipo.IsActive ?? tipo.ativo)
                        ? "Sim"
                        : "Não"}
                    </TableCell>
                    <TableCell align="center">
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1,
                          justifyContent: "center",
                        }}
                      >
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => iniciarEdicao(tipo)}
                          aria-label={`Editar ${tipo.nome}`}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => abrirDialogDelete(tipo)}
                          aria-label={`Excluir ${tipo.nome}`}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
      {/* Dialog de confirmação de exclusão */}
      <Dialog
        open={deleteDialogOpen}
        onClose={fecharDialogDelete}
        fullWidth
        PaperProps={{
          sx: { m: { xs: 2, sm: 3 }, width: { xs: "calc(100% - 32px)", sm: "auto" } },
        }}
      >
        <DialogTitle>Confirmação</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Deseja realmente deletar esse tipo de licença?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={fecharDialogDelete} disabled={deleting}>
            Não
          </Button>
          <Button
            onClick={confirmarDelete}
            color="error"
            variant="contained"
            disabled={deleting}
          >
            {deleting ? <CircularProgress size={18} /> : "Sim, deletar"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdicionarTipoLicenca;
